import { Status as CustomerStatus } from '@brighterly/lib-core-types/customer/enums'

import { useCustomerStore } from '@entities/customer'
import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

import { LessonsActionModal, LessonsDeleteModal } from './enums'
import { useLessonsStore } from './store'
import { useLessonPrivileges } from './lesson-privileges.hook'
import { isReservedLesson } from './lessons'

interface LessonsControlsOptions {
  isLoading: Ref<boolean>
}

interface LessonsControlsReturn {
  refIsShown: ComputedRef<boolean>
  refHasSelection: ComputedRef<boolean>
  refSelectedCount: ComputedRef<number>
  refCanDelete: ComputedRef<boolean>
  refIsDeal: ComputedRef<boolean>
  refIsBulkEditDisabled: ComputedRef<boolean>
  refBulkEditHint: ComputedRef<string>
  refIsDeleteSelectedDisabled: ComputedRef<boolean>
  refDeleteSelectedHint: ComputedRef<string>
  refIsCancelReservedDisabled: ComputedRef<boolean>
  refCancelReservedHint: ComputedRef<string>
  refAddLessonUrl: ComputedRef<string | undefined>
  loadingHint: string
  openDeleteSelected: () => void
  openDeleteReserved: () => void
  openBulkEdit: () => void
}

const LOADING_HINT = 'Wait until the lessons finish loading'
const BULK_EDIT_MIXED_HINT = 'You cannot edit selected lessons with lessons that have the status reserved'
const DELETE_RESERVED_HINT = 'You cannot delete selected lessons with lessons that have the status reserved'
const NO_RESERVED_HINT = 'There are no reserved lessons on this page'

/** The buttons right of the filters: Add lesson and Cancel reserved, or the bulk edit and delete once rows are
 *  ticked. Each disabled button explains why: the list is loading, or the selection rules forbid it. */
export const useLessonsControls = (options: LessonsControlsOptions): LessonsControlsReturn => {
  const route = useRoute()
  const store = useLessonsStore()
  const customerStore = useCustomerStore()
  const { canManageBookings, canDeleteBookings } = useLessonPrivileges()

  const createUrl = resolveUrl(AppRoute.LessonsCreate, { id: route.params.id as string }) ?? ''

  /** The legacy tab showed these controls to lesson managers only. */
  const refIsShown = computed<boolean>(() => canManageBookings.value)

  const refHasSelection = computed<boolean>(() => store.hasSelection)

  const refSelectedCount = computed<number>(() => store.getSelectedIds.length)

  const refHasReservedSelected = computed<boolean>(() => store.hasReservedSelected)

  const refCanDelete = computed<boolean>(() => canDeleteBookings.value)

  /** Reserved and other lessons cannot be edited together: the API refuses the mix. */
  const refIsSelectionMixed = computed<boolean>(() => {
    const selected = store.getLessons.filter(lesson => store.getSelectedIds.includes(lesson.id))
    const reservedCount = selected.filter(lesson => isReservedLesson(lesson.status)).length

    return reservedCount > 0 && reservedCount < selected.length
  })

  const refIsDeal = computed<boolean>(() => customerStore.getCustomer?.status === CustomerStatus.Deal)

  const refHasReservedLessons = computed<boolean>(() => store.getReservedLessons.length > 0)

  const refIsBulkEditDisabled = computed<boolean>(() => options.isLoading.value || refIsSelectionMixed.value)

  const refBulkEditHint = computed<string>(() => refIsSelectionMixed.value ? BULK_EDIT_MIXED_HINT : LOADING_HINT)

  const refIsDeleteSelectedDisabled = computed<boolean>(() => options.isLoading.value || refHasReservedSelected.value)

  const refDeleteSelectedHint = computed<string>(() => refHasReservedSelected.value ? DELETE_RESERVED_HINT : LOADING_HINT)

  const refIsCancelReservedDisabled = computed<boolean>(() => options.isLoading.value || !refHasReservedLessons.value)

  const refCancelReservedHint = computed<string>(() => refHasReservedLessons.value ? LOADING_HINT : NO_RESERVED_HINT)

  /** A link cannot be disabled, so a loading list turns Add lesson into a disabled button. */
  const refAddLessonUrl = computed<string | undefined>(() => options.isLoading.value ? undefined : createUrl)

  const openDeleteSelected = (): void => {
    store.openDeleteModal(LessonsDeleteModal.Selected)
  }

  const openDeleteReserved = (): void => {
    store.openDeleteModal(LessonsDeleteModal.Reserved)
  }

  const openBulkEdit = (): void => {
    store.openActionModal(LessonsActionModal.BulkEdit)
  }

  return {
    refIsShown,
    refHasSelection,
    refSelectedCount,
    refCanDelete,
    refIsDeal,
    refIsBulkEditDisabled,
    refBulkEditHint,
    refIsDeleteSelectedDisabled,
    refDeleteSelectedHint,
    refIsCancelReservedDisabled,
    refCancelReservedHint,
    refAddLessonUrl,
    loadingHint: LOADING_HINT,
    openDeleteSelected,
    openDeleteReserved,
    openBulkEdit,
  }
}
